import React, { useCallback, useImperativeHandle, useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import { parseActivityAnchorId } from '@aline/common';
import { Skeleton, Typography } from '@mui/material';
import type { OnSubmitCommentReturn } from 'src/components';
import { useArrivalHighlightStyles } from 'src/editor/toolbars/right-panel/document-summary/use-arrival-highlight';
import { Apollo } from 'src/gql/generated';
import { domElIdCreators, formatRelativeTime } from 'src/helpers';
import { useAlineMeasure, useIsScrolledPast } from 'src/hooks/dom-hooks';
import { makeStyles } from 'tss-react/mui';
import { Virtualizer } from 'virtua';

import type { ActivityItemFragment } from './activity-types';
import {
  getIsAgentThreadActivity,
  getIsCommentActivity,
  getIsStatusUpdateActivity,
} from './activity-types';
import { ActivityAgentThreadCard } from './ActivityAgentThreadCard';
import { ActivityCommentCard } from './ActivityCommentCard';
import { ActivityEmailEntryCard } from './ActivityEmailEntryCard';
import { ActivityMentionContextProvider } from './ActivityMentionContextProvider';
import { ActivityRowReveal } from './ActivityRowReveal';
import { ActivityStatusRow } from './ActivityStatusRow';
import { getActivityDayLabel, getEmailThread, getIsSameActivityDay } from './email-thread';
import { useActivityFeedViewport } from './use-activity-feed-viewport';
import type { ActivityNavigation } from './use-activity-navigation';
import { useArrivingRowIds } from './use-arriving-row-ids';
import { useHoldInPlace } from './use-hold-in-place';
import { useListStartOffset } from './use-list-start-offset';

/**
 * Rendered beyond each edge of the viewport, so tall rows are measured before they scroll in.
 *
 * Deliberately no `itemSize` alongside it: comment cards run from one line to a whole thread, so
 * every assumed height would have to be corrected under the reader.
 */
const VIEWPORT_OVERSCAN_PX = 600;

/** Space held open at each loadable end of the window, whether or not a page is on its way. */
const GUTTER_HEIGHT = 64;
const GUTTER_SKELETON_ROWS = 1;

const useStyles = makeStyles()(theme => ({
  dayHeading: {
    color: theme.appColors.iconsType,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '16px',
    padding: theme.spacing(0.5, 0.5, 1.5),
  },
  empty: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3, 0),
    textAlign: 'center',
  },
  fill: {
    flex: 1,
    minHeight: 0,
  },
  gutter: {
    boxSizing: 'border-box',
    height: GUTTER_HEIGHT,
    overflow: 'hidden',
    paddingBottom: theme.spacing(1.5),
  },
  header: {
    color: theme.appColors.iconsType,
    fontWeight: 600,
  },
  headerLead: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  headerRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    minHeight: 32,
  },
  // The feed's content when an outer element does the scrolling.
  inScroller: {
    width: '100%',
  },
  leading: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    width: '100%',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.75),
    position: 'relative',
    width: '100%',
  },
  // A copy of the header held at the top of the feed once the real one scrolls away.
  pinnedHeader: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`,
    left: 0,
    paddingBottom: theme.spacing(0.5),
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 1,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    position: 'relative',
    width: '100%',
  },
  row: {
    paddingBottom: theme.spacing(1.75),
  },
  scroller: {
    flex: 1,
    minHeight: 0,
    overflowAnchor: 'none',
    overflowY: 'auto',
    width: '100%',
  },
  skeletonBody: {
    flex: 1,
    minWidth: 0,
  },
  skeletonIconSlot: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  skeletonRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
  },
}));

export interface ActivityFeedHandle {
  /** Jump to the newest row — e.g. after the reader posts their own comment. */
  scrollToBottom: () => void;
}

interface ActivityFeedProps {
  items: ActivityItemFragment[];
  navigation: ActivityNavigation;
  onViewThread: (threadId: UUID) => void;
  workflowId: UUID;
  fetchMoreActivity?: () => Promise<unknown>;
  fetchPreviousActivity?: () => Promise<unknown>;
  hasMore?: boolean;
  hasPrevious?: boolean;
  /** Rendered on the right of the "Activity" heading (e.g. the subscriber control). */
  headerAction?: React.ReactNode;
  /** Rendered just after the "Activity" heading (e.g. the Team / External lane switch). */
  headerControl?: React.ReactNode;
  /**
   * Side padding inside the scroller, so the content can be inset or centred while the scrollbar
   * stays at the scroller's edge. Percentages resolve against the scroller's width.
   */
  inlinePadding?: string;
  /**
   * Content above the virtualized timeline in the same scroller (e.g. task fields). Its measured
   * height becomes Virtua's `startMargin`.
   */
  leadingContent?: React.ReactNode;
  /**
   * The anchor whose window is on screen. A deep-linked row is placed only once this matches the
   * navigation target, so the feed centres on the balanced window the server built around it rather
   * than a stale window a pending anchored fetch is about to replace.
   */
  loadedAnchor?: UUID | null;
  loading?: boolean;
  /** A newer-page fetch is in flight — prevents duplicate infinite-load calls. */
  loadingMore?: boolean;
  /** An older-page fetch is in flight. */
  loadingPrevious?: boolean;
  /** When set, an email thread shows as a compact entry that opens the thread with this id. */
  onOpenEmailThread?: (activityItemId: UUID) => void;
  onSubmitReply?: (parentCommentId: UUID, markdown: string) => Promise<OnSubmitCommentReturn>;
  /**
   * Set to false when content above the feed should be read first — a task's description, say. The
   * feed then opens where its scroller already sits and only follows new rows once the reader has
   * scrolled to the newest edge themselves.
   */
  opensAtNewest?: boolean;
  ref?: React.Ref<ActivityFeedHandle>;
  /**
   * Scroll with this element, e.g. the whole page, instead of a scroller of the feed's own.
   * Everything above the feed inside it becomes the list's start margin.
   */
  scrollElement?: HTMLElement;
  /** Head each day's first row with "Today", "Yesterday" or its date. */
  showDayHeadings?: boolean;
  /** Set to false when the parent already renders an "Activity" heading (e.g. the panel header). */
  showHeader?: boolean;
  /** Keep the heading in view at the top of the feed once it scrolls past. */
  stickyHeader?: boolean;
}

interface ActivityRowProps {
  item: ActivityItemFragment;
  onViewThread: (threadId: UUID) => void;
  workflowId: UUID;
  onOpenEmailThread?: (activityItemId: UUID) => void;
  onSubmitReply?: (parentCommentId: UUID, markdown: string) => Promise<OnSubmitCommentReturn>;
}

interface ActivityVirtualRowProps extends ActivityRowProps {
  arriving: boolean;
  dayLabel: string | null;
  highlighted: boolean;
  onArrived: (id: string) => void;
}

const ActivityVirtualRow = React.memo(function ActivityVirtualRow({
  arriving,
  dayLabel,
  highlighted,
  item,
  onArrived,
  onOpenEmailThread,
  onSubmitReply,
  onViewThread,
  workflowId,
}: ActivityVirtualRowProps) {
  const { classes } = useStyles();
  const { classes: arrivalClasses } = useArrivalHighlightStyles();
  const handleRevealed = useCallback(() => onArrived(item.id), [item.id, onArrived]);

  return (
    <ActivityRowReveal arriving={arriving} onRevealed={handleRevealed}>
      <div className={classes.row}>
        {dayLabel && (
          <Typography className={classes.dayHeading} component="h3">
            {dayLabel}
          </Typography>
        )}
        <div
          className={arrivalClasses.flushHighlightTarget}
          data-arrival-highlighted={highlighted ? 'true' : undefined}
        >
          <ActivityRow
            item={item}
            workflowId={workflowId}
            onOpenEmailThread={onOpenEmailThread}
            onSubmitReply={onSubmitReply}
            onViewThread={onViewThread}
          />
        </div>
      </div>
    </ActivityRowReveal>
  );
});

export function ActivityFeed({
  fetchMoreActivity,
  fetchPreviousActivity,
  hasMore = false,
  hasPrevious = false,
  headerAction,
  headerControl,
  inlinePadding,
  items,
  leadingContent,
  loadedAnchor = null,
  loading = false,
  loadingMore = false,
  loadingPrevious = false,
  navigation,
  onOpenEmailThread,
  onSubmitReply,
  onViewThread,
  opensAtNewest = true,
  ref,
  scrollElement,
  showDayHeadings = false,
  showHeader = true,
  stickyHeader = false,
  workflowId,
}: ActivityFeedProps): JSX.Element {
  const { classes, cx } = useStyles();
  const [scrollerEl, setScrollerEl] = useState<HTMLDivElement | null>(null);
  const [listStartEl, setListStartEl] = useState<HTMLDivElement | null>(null);
  const listStartOffset = useListStartOffset({
    marker: scrollElement ? listStartEl : null,
    scrollElement: scrollElement ?? null,
  });
  const scrollRef = useMemo(
    () => (scrollElement ? { current: scrollElement } : undefined),
    [scrollElement]
  );
  const [headerEl, setHeaderEl] = useState<HTMLDivElement | null>(null);
  const isHeaderScrolledPast = useIsScrolledPast({
    enabled: stickyHeader && showHeader,
    scroller: scrollerEl,
    target: headerEl,
  });
  const [setLeadingRef, leadingMeasurements] = useAlineMeasure<HTMLDivElement>();
  const rowIds = useMemo(() => items.map(item => item.id), [items]);
  const targetIndex = navigation.request
    ? findItemIndexForHash(items, navigation.request.hash)
    : null;
  const resolvedTargetIndex = targetIndex === -1 ? null : targetIndex;
  /**
   * Withhold the row from the viewport until the window on screen is the one anchored on it. A
   * server anchor (`#activity:<id>`) is placed only on its own window; a legacy `#comment:` link,
   * which carries no server anchor, is placed against whatever window already holds it.
   */
  const navigationReady =
    !navigation.request ||
    navigation.request.activityItemId === null ||
    loadedAnchor === navigation.request.activityItemId;
  const showOlderGutter = hasPrevious || loadingPrevious;
  const showLeading = Boolean(leadingContent || showHeader || showOlderGutter);
  const startMargin = getStartMargin({
    leadingHeight: showLeading ? (leadingMeasurements?.height ?? null) : 0,
    listStartOffset,
    scrollsInElement: Boolean(scrollElement),
  });
  const viewport = useActivityFeedViewport({
    endsAtFeedEnd: !scrollElement,
    fetchMore: fetchMoreActivity,
    fetchPrevious: fetchPreviousActivity,
    hasMore,
    hasPrevious,
    isLastRowComment: items.at(-1)?.itemType === Apollo.ActivityItemType.Comment,
    layoutReady: startMargin !== null,
    loadingMore,
    loadingPrevious,
    navigationRequest: navigation.request,
    navigationScopeId: navigation.scopeId,
    opensAtNewest,
    rowIds,
    targetIndex: navigationReady ? resolvedTargetIndex : null,
  });

  const { arrivingIds, onArrived } = useArrivingRowIds({
    enabled: Boolean(scrollElement),
    hasMore,
    rowIds,
  });
  const [listEndEl, setListEndEl] = useState<HTMLDivElement | null>(null);
  /**
   * In a shared scroller the newest row sits above whatever follows the feed, so it only needs to be
   * on screen. Aligning it to the bottom would push the content below it, e.g. the composer, away.
   */
  const revealListEnd = useCallback(
    () => listEndEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
    [listEndEl]
  );
  const scrollToBottom = scrollElement ? revealListEnd : viewport.showLatest;

  useHoldInPlace({
    holding: arrivingIds.size > 0,
    marker: listEndEl,
    scrollElement: scrollElement ?? null,
  });
  useImperativeHandle(ref, () => ({ scrollToBottom }), [scrollToBottom]);

  const renderItem = useCallback(
    (item: ActivityItemFragment, index: number) => (
      <ActivityVirtualRow
        key={item.id}
        arriving={arrivingIds.has(item.id)}
        dayLabel={showDayHeadings ? getDayLabelForRow(items, index) : null}
        highlighted={navigation.isRinging && item.id === items[targetIndex ?? -1]?.id}
        item={item}
        workflowId={workflowId}
        onArrived={onArrived}
        onOpenEmailThread={onOpenEmailThread}
        onSubmitReply={onSubmitReply}
        onViewThread={onViewThread}
      />
    ),
    [
      arrivingIds,
      items,
      navigation.isRinging,
      onArrived,
      onOpenEmailThread,
      onSubmitReply,
      onViewThread,
      showDayHeadings,
      targetIndex,
      workflowId,
    ]
  );

  const header = (
    <>
      <div className={classes.headerLead}>
        <Typography className={classes.header} variant="subtitle2">
          Activity
        </Typography>
        {headerControl}
      </div>
      {headerAction}
    </>
  );

  const paddingStyle = inlinePadding
    ? { paddingLeft: inlinePadding, paddingRight: inlinePadding }
    : undefined;

  return (
    <div className={cx(classes.root, !scrollElement && classes.fill)}>
      {isHeaderScrolledPast && (
        <div className={cx(classes.headerRow, classes.pinnedHeader)} style={paddingStyle}>
          {header}
        </div>
      )}
      <div
        ref={setScrollerEl}
        className={scrollElement ? classes.inScroller : classes.scroller}
        data-activity-scroller="true"
        style={paddingStyle}
      >
        {showLeading && (
          <div ref={setLeadingRef} className={classes.leading}>
            {leadingContent}
            {showHeader && (
              <div ref={setHeaderEl} className={classes.headerRow}>
                {header}
              </div>
            )}
            {showOlderGutter && <ActivityFeedGutter loading={loadingPrevious} placement="older" />}
          </div>
        )}
        <ActivityMentionContextProvider items={items}>
          {!loading && !items.length && (
            <div className={classes.empty}>
              <Typography align="center" variant="body2">
                No activity
              </Typography>
            </div>
          )}
          {loading && !items.length && <ActivityFeedSkeleton />}
          {Boolean(items.length) && (
            <>
              {Boolean(scrollElement) && <div ref={setListStartEl} />}
              <Virtualizer
                ref={viewport.virtualizerRef}
                bufferSize={VIEWPORT_OVERSCAN_PX}
                data={items}
                scrollRef={scrollRef}
                shift={viewport.shift}
                startMargin={startMargin ?? 0}
                onScroll={viewport.onScroll}
              >
                {renderItem}
              </Virtualizer>
              {Boolean(scrollElement) && <div ref={setListEndEl} />}
              {(hasMore || loadingMore) && (
                <ActivityFeedGutter loading={loadingMore} placement="newer" />
              )}
            </>
          )}
        </ActivityMentionContextProvider>
      </div>
    </div>
  );
}

function ActivityRow({
  item,
  onOpenEmailThread,
  onSubmitReply,
  onViewThread,
  workflowId,
}: ActivityRowProps): JSX.Element | null {
  /**
   * AI-sourced rows are attributed to the acting user's id for audit, but should display as the
   * "Aline AI" entity rather than that person.
   */
  const isAi = item.sourceType === Apollo.ActivitySourceType.Ai;

  // Distinct from the `#activity:<id>` fragment so the browser does not steal scroll-to-top.
  const anchored = (content: JSX.Element) => (
    <div id={domElIdCreators.activityRowEl(item.id)}>{content}</div>
  );

  const emailThread = onOpenEmailThread ? getEmailThread(item) : null;
  if (emailThread && onOpenEmailThread)
    return anchored(<ActivityEmailEntryCard thread={emailThread} onOpen={onOpenEmailThread} />);

  if (getIsCommentActivity(item.body)) {
    const { comment } = item.body;
    if (!comment || comment.parent) return null;

    return anchored(
      <ActivityCommentCard
        activityItemId={item.id}
        comment={comment}
        email={item.channelThread?.email}
        isAi={isAi}
        slackThreadUrl={item.channelThread?.webUrl}
        sourceType={item.sourceType}
        workflowId={workflowId}
        onSubmitReply={onSubmitReply}
      />
    );
  }

  if (getIsAgentThreadActivity(item.body)) {
    const { agentThread } = item.body;
    if (!agentThread) return null;
    return anchored(
      <ActivityAgentThreadCard
        agentThread={agentThread}
        canStop={agentThread.requesterCanEdit}
        time={formatRelativeTime(item.createdAt)}
        onViewThread={onViewThread}
      />
    );
  }

  if (getIsStatusUpdateActivity(item.body))
    return anchored(
      <ActivityStatusRow
        author={item.author}
        body={item.body}
        isAi={isAi}
        time={formatRelativeTime(item.createdAt)}
      />
    );

  return null;
}

/** Matches the fragment a link carried — `activity:<id>`, or a legacy `comment:<id>` — to a row. */
function findItemIndexForHash(items: ActivityItemFragment[], commentHash: string): number {
  const activityItemId = parseActivityAnchorId(commentHash);
  if (activityItemId) return items.findIndex(item => item.id === activityItemId);

  const commentId = commentHash.startsWith('comment:')
    ? commentHash.slice('comment:'.length)
    : commentHash;

  return items.findIndex(item => {
    if (!getIsCommentActivity(item.body)) return false;
    const { comment } = item.body;
    if (!comment) return false;
    // A reply has no row of its own — it renders inside its parent's card, so that row is the target.
    return comment.id === commentId || comment.replies.some(reply => reply.id === commentId);
  });
}

/** The day heading for a row that starts a new day in the loaded window, else null. */
function getDayLabelForRow(items: ActivityItemFragment[], index: number): string | null {
  const item = items[index];
  if (!item) return null;
  const previous = items[index - 1];
  if (previous && getIsSameActivityDay(previous.createdAt, item.createdAt)) return null;
  return getActivityDayLabel(new Date(item.createdAt));
}

/**
 * Where the first row starts in the scroller, or null until it is known. In its own scroller the
 * list starts below the leading content; in an outer scroller, below everything above the feed.
 */
function getStartMargin({
  leadingHeight,
  listStartOffset,
  scrollsInElement,
}: {
  leadingHeight: number | null;
  listStartOffset: number | null;
  scrollsInElement: boolean;
}): number | null {
  if (scrollsInElement) return listStartOffset;
  return leadingHeight;
}

/** Fixed-height space at one loadable end; height must not depend on the loader. */
function ActivityFeedGutter({
  loading = false,
  placement,
}: {
  placement: 'newer' | 'older';
  loading?: boolean;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.gutter} data-activity-gutter={placement}>
      {loading && <ActivityFeedSkeleton rows={GUTTER_SKELETON_ROWS} />}
    </div>
  );
}

function ActivityFeedSkeleton({ rows = 4 }: { rows?: number } = {}): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.list}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={classes.skeletonRow}>
          <span className={classes.skeletonIconSlot}>
            <Skeleton height={20} variant="circular" width={20} />
          </span>
          <div className={classes.skeletonBody}>
            <Skeleton height={18} variant="text" width="60%" />
            <Skeleton height={18} variant="text" width="90%" />
          </div>
        </div>
      ))}
    </div>
  );
}
